import {
  Container,
  DialogTrigger,
  LinkButton,
  Popover,
  PopoverContent,
  showToast,
  TagGroupSelectFormikField,
  TextInputFormikField,
  Text as StepHeadingText,
} from "@certn/cascadia-react";
import { Stack, Text } from "@certn/cascadia";
import { OrderStepLayout } from "../components/OrderStepLayout";
import {
  PlaceOrderDisabledReasons,
  getPlaceOrderDisabledReasons,
} from "../components/PlaceOrderDisabledReasons";
import { useUser } from "../../../hooks/useUser";
import { useClientPortalAPI, useSubmit } from "@certn/api";
import { Trans, useTranslation } from "react-i18next";
import { useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { OrderContext } from "../OrderContext";
import { clearOrderDraft, useOrderDraftKey } from "../orderDraftStorage";
import {
  orderContextToQuickscreenPayload,
  showSubmitOrderError,
} from "../order-utils";
import { Formik } from "formik";
import { object, string } from "yup";
import { RegionProvider, RegionContext } from "@certn/utils";
import {
  OrderReviewSummaryBox,
  isMissingCardOnFile,
} from "../../../components/ordering-review/OrderReviewSummaryBox";
import { useOrderBillingProfile } from "./useOrderBillingProfile";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import * as Sentry from "@sentry/react";
import { useFocusStepHeading } from "../useFocusStepHeading";

export function ReviewStepQuickscreen() {
  const { order, setOrder } = useContext(OrderContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const stepHeadingId = useFocusStepHeading();
  const { details: userDetails } = useUser();
  const orderDraftKey = useOrderDraftKey();
  const { billingRequireCardOnFileForOrder } = useFlags();
  const { data: billingProfile } = useOrderBillingProfile();
  const { submit, isSubmitting } = useSubmit(
    `/api/internal/client-portal/quickscreen-case/`,
    {
      throwErrors: true,
    }
  );
  const { data: tags } = useClientPortalAPI(
    `/api/internal/client-portal/tags/active/`
  );
  const tagsList = tags?.concat(order.tags);
  const uniqueTagsMap = new Map(tagsList?.map((tag) => [tag.id, tag]));
  const uniqueTagsList = [...uniqueTagsMap.values()];

  const formSchema = object().shape({
    applicantEmail: string()
      .required(t("This cannot be blank."))
      .email(t("Invalid email address.")),
  });

  const handleEmailChange = useCallback(
    (value: string) => {
      const isValid = string().email().isValidSync(value);
      const recipients = isValid ? [value] : [];
      setOrder((prev) => ({ ...prev, recipients }));
    },
    [setOrder]
  );

  const handleTagsChange = useCallback(
    (tags: Record<string, any>[]) => {
      setOrder((prev) => ({
        ...prev,
        tags: tags as { id: string; tagValue: string }[],
      }));
    },
    [setOrder]
  );

  const submitOrder = async () => {
    try {
      const response = (await submit(
        orderContextToQuickscreenPayload(
          order,
          userDetails,
          i18n.resolvedLanguage ?? i18n.language
        )
      )) as { short_id: string };
      showToast(t("Order submitted"), { type: "info" });
      clearOrderDraft(orderDraftKey);
      navigate(`/quickscreen/${response.short_id}`, { replace: true });
    } catch (e) {
      Sentry.captureException(e);
      showSubmitOrderError(e, t);
    }
  };

  const missingCardOnFile = isMissingCardOnFile(
    billingProfile,
    !!billingRequireCardOnFileForOrder
  );
  const disabledReasons = getPlaceOrderDisabledReasons({
    isMissingCardOnFile: missingCardOnFile,
  });

  return (
    <Formik
      initialValues={{
        applicantEmail: order.recipients[0] ?? "",
        tags: order.tags,
      }}
      onSubmit={() => {
        /* submission handled by submitOrder */
      }}
      validationSchema={formSchema}
    >
      {() => {
        return (
          <OrderStepLayout
            onComplete={submitOrder}
            disableCompletion={
              order.recipients.length === 0 || disabledReasons.length > 0
            }
            submitting={isSubmitting}
            scrollRegionAriaLabel={t("Order review content")}
          >
            <Container layout="wide">
              <Stack gap="space-1">
                <StepHeadingText
                  id={stepHeadingId}
                  as="h1"
                  tabIndex={-1}
                  style="global/title-small"
                >
                  {t("Review and create case")}
                </StepHeadingText>
                <Text style="global/body-secondary">
                  {t(
                    "Once you press Send, the case will be created and you can begin filling out the application on behalf of the applicant."
                  )}
                </Text>
              </Stack>
              <div className={styles.mainContentContainer}>
                <Stack gap="space-6">
                  <PlaceOrderDisabledReasons reasons={disabledReasons} />
                  <TextInputFormikField
                    name="applicantEmail"
                    label={t("Applicant email")}
                    testID="applicant-email-input"
                    isRequired
                    type="email"
                    onChange={handleEmailChange}
                  />
                  <TagGroupSelectFormikField
                    label={t("Tags")}
                    name="tags"
                    testID="tag-select-field"
                    values={uniqueTagsList || []}
                    selectedValues={order.tags}
                    setSelectedValues={() => {
                      /* selection handled by onChange */
                    }}
                    onChange={handleTagsChange}
                    info={
                      <DialogTrigger>
                        <LinkButton testID="tags-whats-this-link">
                          {t("What's this?")}
                        </LinkButton>
                        <Popover placement="top right">
                          <PopoverContent>
                            <Trans>
                              Tags allow you to categorize and identify themes
                              in your orders. Tags can be managed within the
                              group settings and they will be visible to all
                              members within the group. Applicants will not see
                              this.
                            </Trans>
                          </PopoverContent>
                        </Popover>
                      </DialogTrigger>
                    }
                  />
                </Stack>

                <RegionProvider>
                  <RegionContext.Consumer>
                    {(region) => (
                      <OrderReviewSummaryBox
                        order={order}
                        region={region}
                        onPlaceOrder={submitOrder}
                        isSubmitting={isSubmitting}
                        disablePlaceOrder={disabledReasons.length > 0}
                        isQuickscreen={true}
                      />
                    )}
                  </RegionContext.Consumer>
                </RegionProvider>
              </div>
            </Container>
          </OrderStepLayout>
        );
      }}
    </Formik>
  );
}

const styles = {
  mainContentContainer: css({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "var(--space-6)",
    marginTop: "var(--space-6)",
  }),
};
